import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { NotificationItem } from "./notification-item";
import { useMarkRead, useNotificationFeed } from "./use-notifications";

/**
 * The notification-center body: header (title + mark-all-read), the scrollable
 * feed list with loading / empty states, and a load-older control. Rendered
 * inside the desktop popover and the mobile sheet alike.
 */
export function NotificationPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const feed = useNotificationFeed(open);
  const markRead = useMarkRead();

  const rows = feed.data?.pages.flatMap((p) => p.notifications) ?? [];
  const hasUnread = rows.some((n) => !n.read_at);

  return (
    <div className="flex max-h-[min(32rem,70vh)] flex-col">
      <div className="flex items-center justify-between border-b border-grey-soft px-4 py-3">
        <span className="text-base font-semibold text-foreground">Notifications</span>
        {hasUnread && (
          <button
            type="button"
            onClick={() => markRead.mutate(undefined)}
            className="cursor-pointer text-sm font-semibold text-primary hover:underline"
          >
            Mark all as read
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {feed.isLoading ? (
          <div className="flex flex-col gap-3 p-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2 py-1">
                  <Skeleton className="h-3.5 w-2/3" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8">
            <EmptyState
              icon="calendar"
              title="You're all caught up"
              message="Reminders and card activity will show up here."
            />
          </div>
        ) : (
          <ul className="divide-y divide-grey-soft">
            {rows.map((n) => (
              <li key={n.notification_id} className="list-none">
                <NotificationItem
                  notification={n}
                  onMarkRead={(id) => markRead.mutate(id)}
                  onNavigated={onClose}
                />
              </li>
            ))}
          </ul>
        )}

        {feed.hasNextPage && (
          <div className="border-t border-grey-soft p-2">
            <button
              type="button"
              onClick={() => void feed.fetchNextPage()}
              disabled={feed.isFetchingNextPage}
              className="w-full cursor-pointer rounded-md py-2 text-sm font-semibold text-primary hover:bg-muted disabled:opacity-50"
            >
              {feed.isFetchingNextPage ? "Loading…" : "Load older notifications"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
